import React, { useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  LinearProgress,
  Paper,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Autocomplete,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import {
  DocumentFoldersClient,
  DocumentsClient,
  DocumentFolderDto,
  formatFileSize,
} from '../../app/services/api/documentsClient';
import {
  TaskTemplatesClient,
  TaskTemplateDto,
} from '../../app/services/api/tasksClient';

const foldersClient = new DocumentFoldersClient();
const documentsClient = new DocumentsClient();
const templatesClient = TaskTemplatesClient;

interface FileUploadItem {
  file: File;
  name: string;
  description: string;
  tags: string[];
  linkedTaskTemplateIds: number[];
  progress: number;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  error?: string;
}

const DocumentUpload: React.FC = () => {
  const { t } = useTranslation('documents');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { hasPermissionTo } = useContext(UserContext);

  const canUpload = hasPermissionTo ? hasPermissionTo(['DocumentsUpload']) : true;

  const folderId = searchParams.get('folderId');

  const [loading, setLoading] = useState(true);
  const [folder, setFolder] = useState<DocumentFolderDto | null>(null);
  const [templates, setTemplates] = useState<TaskTemplateDto[]>([]);
  const [files, setFiles] = useState<FileUploadItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (folderId) {
          const f = await foldersClient.getFolder(Number(folderId));
          setFolder(f);
        }
        const t = await templatesClient.getTemplates();
        setTemplates(t);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [folderId]);

  const getDocumentIcon = (file: File) => {
    if (file.type === 'application/pdf') return <PictureAsPdfIcon color="error" />;
    if (file.type.startsWith('image/')) return <ImageIcon color="primary" />;
    if (file.type.startsWith('video/')) return <VideoFileIcon color="secondary" />;
    if (file.type.includes('document') || file.type.includes('word')) return <DescriptionIcon color="info" />;
    return <InsertDriveFileIcon />;
  };

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const newFiles: FileUploadItem[] = Array.from(selectedFiles).map(file => ({
      file,
      name: file.name.replace(/\.[^/.]+$/, ''), // Remove extension for display name
      description: '',
      tags: [],
      linkedTaskTemplateIds: [],
      progress: 0,
      status: 'pending',
    }));

    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileChange = (index: number, field: keyof FileUploadItem, value: string | string[] | number[]) => {
    setFiles(prev => prev.map((f, i) =>
      i === index ? { ...f, [field]: value } : f,
    ));
  };

  const handleUpload = async () => {
    if (!folder || files.length === 0) return;

    setUploading(true);

    for (let i = 0; i < files.length; i++) {
      const fileItem = files[i];
      if (fileItem.status !== 'pending') continue;

      // Update status to uploading
      setFiles(prev => prev.map((f, idx) =>
        idx === i ? { ...f, status: 'uploading', progress: 0 } : f,
      ));

      try {
        // Simulate progress
        for (let progress = 0; progress <= 90; progress += 10) {
          await new Promise(resolve => setTimeout(resolve, 50));
          setFiles(prev => prev.map((f, idx) =>
            idx === i ? { ...f, progress } : f,
          ));
        }

        await documentsClient.uploadDocument(
          {
            folderId: folder.id,
            name: fileItem.name,
            description: fileItem.description || undefined,
            tags: fileItem.tags,
            linkedTaskTemplateIds: fileItem.linkedTaskTemplateIds,
          },
          fileItem.file,
        );

        setFiles(prev => prev.map((f, idx) =>
          idx === i ? { ...f, status: 'complete', progress: 100 } : f,
        ));
      } catch (error) {
        setFiles(prev => prev.map((f, idx) =>
          idx === i ? { ...f, status: 'error', error: 'Upload failed' } : f,
        ));
      }
    }

    setUploading(false);
  };

  const allComplete = files.length > 0 && files.every(f => f.status === 'complete');
  const hasPending = files.some(f => f.status === 'pending');

  if (!canUpload) {
    return (
      <PageArea>
        <PageTitle>{t('uploadDocumentsTitle', 'Upload Documents')}</PageTitle>
        <Typography color="error">{t('noUploadPermission', 'You do not have permission to upload documents.')}</Typography>
      </PageArea>
    );
  }

  if (loading) {
    return (
      <PageArea>
        <LinearProgress />
      </PageArea>
    );
  }

  if (!folder) {
    return (
      <PageArea>
        <PageTitle>{t('uploadDocumentsTitle', 'Upload Documents')}</PageTitle>
        <Typography color="error">{t('selectFolderPrompt', 'Please select a folder to upload to.')}</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/documents')}
          sx={{ mt: 2 }}
        >
          {t('backToDocuments', 'Back to Documents')}
        </Button>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton
          aria-label={t('backToDocumentsAriaLabel', 'Back to documents')}
          onClick={() => navigate(`/documents?folderId=${folder.id}`)}
        >
          <ArrowBackIcon />
        </IconButton>
        <PageTitle subTitle={t('uploadToSubtitle', 'Upload to: {{path}}', { path: folder.path.join(' / ') })}>
          {t('uploadDocumentsTitle', 'Upload Documents')}
        </PageTitle>
      </Box>

      <Grid container
        spacing={3}>
        {/* Drop Zone */}
        <Grid item
          xs={12}>
          <Section>
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                border: '2px dashed',
                borderRadius: '10px',
                borderColor: dragOver ? 'primary.main' : 'grey.300',
                backgroundColor: dragOver ? 'action.hover' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                aria-label={t('selectFilesAriaLabel', 'Select files to upload')}
                type="file"
                multiple
                hidden
                onChange={(e) => handleFileSelect(e.target.files)}
              />
              <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography
                variant="displayXS"
                gutterBottom
              >
                {t('dragDropText', 'Drag and drop files here')}
              </Typography>
              <Typography color="text.secondary">
                {t('clickToBrowse', 'or click to browse')}
              </Typography>
              <Typography
                variant="textXS"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                {t('supportedFormats', 'Supported formats: PDF, Images, Documents, Spreadsheets, Presentations')}
              </Typography>
            </Paper>
          </Section>
        </Grid>

        {/* File List */}
        {files.length > 0 && (
          <Grid item
            xs={12}>
            <Section>
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="displayXS">
                  {t('filesToUploadTitle', 'Files to Upload ({{count}})', { count: files.length })}
                </Typography>
              </Box>
              <List>
                {files.map((fileItem, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      py: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 2 }}>
                      <ListItemIcon>
                        {getDocumentIcon(fileItem.file)}
                      </ListItemIcon>
                      <ListItemText
                        primary={fileItem.file.name}
                        secondary={formatFileSize(fileItem.file.size)}
                      />
                      {fileItem.status === 'complete' && (
                        <Chip label={t('uploadedChip', 'Uploaded')}
                          color="success"
                          size="small"
                          sx={{ mr: 2 }} />
                      )}
                      {fileItem.status === 'error' && (
                        <Chip label={t('errorChip', 'Error')}
                          color="error"
                          size="small"
                          sx={{ mr: 2 }} />
                      )}
                      {fileItem.status === 'uploading' && (
                        <Box sx={{ width: 100, mr: 2 }}>
                          <LinearProgress variant="determinate"
                            value={fileItem.progress} />
                        </Box>
                      )}
                      <ListItemSecondaryAction>
                        <IconButton
                          aria-label={t('removeFileAriaLabel', 'Remove file {{name}}', { name: fileItem.file.name })}
                          edge="end"
                          onClick={() => handleRemoveFile(index)}
                          disabled={uploading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </Box>

                    {fileItem.status === 'pending' && (
                      <Grid container
                        spacing={2}
                        sx={{ pl: 7 }}>
                        <Grid item
                          xs={12}
                          md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t('documentNameLabel', 'Document Name')}
                            value={fileItem.name}
                            onChange={(e) => handleFileChange(index, 'name', e.target.value)}
                          />
                        </Grid>
                        <Grid item
                          xs={12}
                          md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label={t('descriptionLabel', 'Description')}
                            value={fileItem.description}
                            onChange={(e) => handleFileChange(index, 'description', e.target.value)}
                          />
                        </Grid>
                        <Grid item
                          xs={12}
                          md={6}>
                          <Autocomplete
                            multiple
                            freeSolo
                            size="small"
                            options={['training', 'policy', 'guide', 'manual', 'compliance']}
                            value={fileItem.tags}
                            onChange={(_, value) => handleFileChange(index, 'tags', value)}
                            renderTags={(value, getTagProps) =>
                              value.map((option, i) => (
                                <Chip
                                  {...getTagProps({ index: i })}
                                  size="small"
                                  label={option}
                                  key={option}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('tagsLabel', 'Tags')}
                                placeholder={t('tagsPlaceholder', 'Add tags...')}
                              />
                            )}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={6}
                        >
                          <Autocomplete
                            multiple
                            size="small"
                            options={templates}
                            getOptionLabel={(option) => option.name}
                            value={templates.filter(t => fileItem.linkedTaskTemplateIds.includes(t.id))}
                            onChange={(_, value) => handleFileChange(index, 'linkedTaskTemplateIds', value.map(v => v.id))}
                            renderTags={(value, getTagProps) =>
                              value.map((option, i) => (
                                <Chip
                                  {...getTagProps({ index: i })}
                                  size="small"
                                  label={option.name}
                                  key={option.id}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('linkTaskTemplatesLabel', 'Link to Task Templates')}
                                placeholder={t('selectTemplatesPlaceholder', 'Select templates...')}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </ListItem>
                ))}
              </List>

              <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/documents?folderId=${folder.id}`)}
                  disabled={uploading}
                >
                  {t('cancel', 'Cancel')}
                </Button>
                {allComplete ? (
                  <Button
                    variant="primary"
                    onClick={() => navigate(`/documents?folderId=${folder.id}`)}
                  >
                    {t('doneButton', 'Done')}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    onClick={handleUpload}
                    disabled={uploading || !hasPending}
                    startIcon={<CloudUploadIcon />}
                  >
                    {uploading ? t('uploading', 'Uploading...') : t('uploadAllButton', 'Upload All')}
                  </Button>
                )}
              </Box>
            </Section>
          </Grid>
        )}
      </Grid>
    </PageArea>
  );
};

export default DocumentUpload;
